/**
 * Removes Personal Data (PD) from a URL query string before it is forwarded
 * to third-party systems such as Statsig as a user attribute.
 *
 * Atlassian's PD Standard
 * (https://hello.atlassian.net/wiki/spaces/PMP/pages/139161617) classifies a
 * number of fields as Restricted/Confidential PD. Several of those fields are
 * frequently appended to wac.atlassian.com URLs by marketing campaigns,
 * onboarding redirects, sign-up flows, etc. (`?email=...`, `?firstName=...`,
 * `?phone=...`, ...), and were being sent verbatim to Statsig via the
 * `queryParams` user attribute.
 *
 * This utility scrubs the query string in two passes:
 *
 *   1. Drops any parameter whose **name** matches a known PD field name
 *      (case-insensitive, supports common separator variants such as
 *      `firstname`, `first_name`, `first-name`, `firstName`).
 *   2. Redacts any parameter whose **value** clearly looks like a PD value
 *      (currently: anything containing an `@` is treated as an email-shaped
 *      value, regardless of the key).
 *
 * The result preserves param ordering and is safe to use as a Statsig user
 * attribute. The function is intentionally pure and side-effect free so it can
 * be exercised on both server and client code paths
 * (see `feature-gates/bootstrapper.ts` and
 * `utils/fg-bootstrap-values-override-utils.ts`).
 */

/**
 * PD field names sourced from the "Atlassian PD Elements" table on
 * https://hello.atlassian.net/wiki/spaces/PMP/pages/139161617#Atlassian-PD-Elements.
 *
 * Names are stored in a normalized form (lowercase, with `_` and `-` removed)
 * so that the lookup matches `firstName`, `first_name`, `first-name`, and
 * `firstname` without having to enumerate every variant.
 *
 * NOTE: this list is deliberately conservative — when in doubt, drop the
 * parameter. Adding a brand-new query param is cheap; leaking a PD value to a
 * third party is not.
 */
const PD_PARAM_NAMES: ReadonlySet<string> = new Set(
	[
		// --- Direct identifiers ---
		'email',
		'emailaddress',
		'useremail',
		'personalemail',
		'workemail',
		'name',
		'fullname',
		'firstname',
		'lastname',
		'givenname',
		'familyname',
		'surname',
		'middlename',
		'displayname',
		'nickname',
		'username',
		'user',
		'userid', // Atlassian account IDs are PD; cloud identifiers come in
		//         on their own dedicated, validated `cloudId` param which
		//         is intentionally NOT scrubbed.
		'login',
		'loginid',
		'phone',
		'phonenumber',
		'mobile',
		'mobilenumber',
		'telephone',
		'tel',
		'fax',
		'address',
		'address1',
		'address2',
		'streetaddress',
		'street',
		'city',
		'state',
		'province',
		'postalcode',
		'postcode',
		'zip',
		'zipcode',
		'country', // request-derived geo is allowed via the dedicated
		//            `country`/`region` user attributes; we do NOT want
		//            user-supplied country values from the URL.
		'billingaddress',
		'shippingaddress',
		'passport',
		'passportnumber',
		'ssn',
		'taxid',
		'nationalid',
		'governmentid',
		'creditcard',
		'cardnumber',
		'ccnumber',
		'cvv',
		'cvc',
		'bankaccount',
		'iban',
		'profilephoto',
		'avatar',
		'avatarurl',
		'photo',
		'photourl',
		'biography',
		'bio',
		'aboutme',
		// --- Indirect identifiers (Restricted) ---
		'jobtitle',
		'title',
		'role',
		'dob',
		'dateofbirth',
		'birthday',
		'birthdate',
		'age',
		'gender',
		'genderidentity',
		'pronouns',
		'preferredpronouns',
		'race',
		'ethnicity',
		'sexualorientation',
		'disability',
		'disabilitystatus',
		'education',
		'educationlevel',
		'ip',
		'ipaddress',
		'ccexp',
		'ccexpiration',
		'creditcardexpiration',
		'creditcardtype',
		// --- Atlassian-specific PD ---
		'aaid',
		'atlassianaccountid',
		'accountid',
		'sen',
		'supportentitlementnumber',
	].map(normalizeParamName),
);

/** Placeholder value substituted in for parameters whose value shape looks
 *  like PD (e.g. an email). The key is preserved so that downstream
 *  experiment targeting that already inspects "did this URL have an `x`
 *  param?" continues to work, but the actual PD value never leaves the
 *  process. */
const REDACTED = '[REDACTED]';

/**
 * Normalizes a query-param name for deny-list comparison: lowercases, and
 * strips common separators so `first_name`, `first-name`, `firstName`, and
 * `firstname` all collapse to the same key.
 */
function normalizeParamName(name: string): string {
	return name.toLowerCase().replace(/[_\-\s]/g, '');
}

/** Returns true if a value looks like an email address. Intentionally cheap
 *  and conservative — we only need to catch obvious leakage, not validate
 *  RFC 5322. */
function looksLikeEmail(value: string): boolean {
	// Any "x@y" with no whitespace is treated as an email-shaped value.
	return /[^\s@]+@[^\s@]+/.test(value);
}

/**
 * Strips PD parameters from a URL query string.
 *
 * @param search The raw query string, with or without a leading `?`
 *   (matches the shape of `location.search`). May be empty or undefined.
 * @returns A sanitized query string with the same leading `?` convention as
 *   the input. Returns an empty string when the input is empty.
 */
export function sanitizeQueryParams(search: string | undefined | null): string {
	if (!search) {
		return '';
	}

	const hasLeadingQuestionMark = search.startsWith('?');
	const raw = hasLeadingQuestionMark ? search.slice(1) : search;
	if (raw.length === 0) {
		return hasLeadingQuestionMark ? '?' : '';
	}

	// URLSearchParams handles decoding and re-encoding for us, and preserves
	// insertion order, which keeps the sanitized string stable for any
	// downstream targeting that hashes the value.
	let params: URLSearchParams;
	try {
		params = new URLSearchParams(raw);
	} catch {
		// If the query string is malformed, the safest behaviour is to drop
		// it entirely rather than risk forwarding PD.
		return hasLeadingQuestionMark ? '?' : '';
	}

	const sanitized = new URLSearchParams();
	for (const [key, value] of params) {
		if (PD_PARAM_NAMES.has(normalizeParamName(key))) {
			// Drop the parameter entirely (key + value).
			continue;
		}
		if (looksLikeEmail(value)) {
			sanitized.append(key, REDACTED);
			continue;
		}
		sanitized.append(key, value);
	}

	const result = sanitized.toString();
	if (result.length === 0) {
		return hasLeadingQuestionMark ? '?' : '';
	}
	return hasLeadingQuestionMark ? `?${result}` : result;
}
